import React, { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Upload, Image as ImageIcon, X } from "lucide-react";

function imageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
}

export default function MediaPicker({ value, onChange, testid = "media-picker" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media", { params: { limit: 60 } });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data, ...prev]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const pick = (m) => {
    onChange?.({ url: m.webp_paths?.md || m.webp_paths?.lg, paths: m.webp_paths, id: m.id });
    setOpen(false);
  };

  const previewUrl = value
    ? imageUrl(typeof value === "string" ? value : value?.md || value?.url)
    : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="border border-dashed border-border rounded-lg p-4 hover:border-primary/40 transition w-full text-left"
          data-testid={`${testid}-trigger`}
        >
          {previewUrl ? (
            <div className="flex items-center gap-3">
              <img src={previewUrl} alt="selected" className="w-16 h-16 object-cover rounded" />
              <div className="text-sm">
                <div className="text-muted-foreground">Image attached</div>
                <div className="text-primary text-xs">Tap to change</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-muted-foreground">
              <ImageIcon className="w-5 h-5" />
              <span className="text-sm">Choose or upload image</span>
            </div>
          )}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Media library</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 border border-dashed border-border rounded-lg cursor-pointer hover:border-primary/40">
            <Upload className="w-4 h-4 text-primary" />
            <span className="text-sm">
              {uploading ? "Uploading…" : "Click to upload image (jpg/png/webp, up to 10 MB)"}
            </span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) upload(e.target.files[0]);
                e.target.value = "";
              }}
              data-testid={`${testid}-upload-input`}
            />
          </label>
          {loading ? (
            <div className="py-10 flex justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto">
              {items.length === 0 && (
                <div className="col-span-4 text-center py-10 text-sm text-muted-foreground">
                  No images yet.
                </div>
              )}
              {items.map((m) => {
                const url = imageUrl(m.webp_paths?.thumb || m.webp_paths?.md);
                return (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => pick(m)}
                    className="group relative aspect-square overflow-hidden rounded-lg border border-border hover:border-primary/60"
                    data-testid={`media-thumb-${m.id}`}
                  >
                    {url ? (
                      <img src={url} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          {value && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onChange?.(null);
                setOpen(false);
              }}
              data-testid={`${testid}-clear`}
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
          <Button type="button" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
